import React, { useMemo, useState } from 'react';
import CircularProgress from '@mui/material/CircularProgress';
import Modal from '@mui/material/Modal';
import Pagination from '@mui/material/Pagination';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import CloseIcon from '@mui/icons-material/Close';
import FilterListIcon from '@mui/icons-material/FilterList';
import RefreshIcon from '@mui/icons-material/Refresh';
import SearchIcon from '@mui/icons-material/Search';
import { splitRiskSignals } from 'utils/entityRiskSignals';

interface IProductScreeningResultsTableProps {
  results: any;
  page: number;
  setPage: any;
  handleSubmit: any;
}

type ProductSortKey =
  | 'productName'
  | 'hsCode'
  | 'listName'
  | 'agency'
  | 'sourceCountry'
  | 'detectionDate';
type SortDirection = 'asc' | 'desc';
type ProductFilterKey = 'hsCode' | 'listName' | 'agency' | 'sourceCountry';

const getSource = (hit: any) => hit?._source || {};

const getValue = (source: any, keys: string[], fallback = '--') => {
  const value = keys.map((key) => source?.[key]).find(Boolean);
  return value || fallback;
};

const hasDisplayValue = (value: any) => {
  if (value === null || value === undefined) return false;
  if (Array.isArray(value)) return value.filter(Boolean).length > 0;
  if (typeof value === 'object') return Object.keys(value).length > 0;
  return String(value).trim() !== '';
};

const formatFieldValue = (value: any): string => {
  if (Array.isArray(value)) return value.filter(Boolean).join(', ');
  if (value && typeof value === 'object') return JSON.stringify(value);
  return String(value ?? '');
};

// Detects a URL-looking token. Accepts bare domains starting with www. and any
// http(s) link. Keep the regex narrow — we render arbitrary record fields and
// don't want stray text turned into links.
const URL_TOKEN_REGEX = /\b((?:https?:\/\/|www\.)[^\s<>"'`]+)/gi;

const renderFieldWithLinks = (value: string): React.ReactNode => {
  if (!value) return value;
  const parts: React.ReactNode[] = [];
  let lastIndex = 0;
  let key = 0;
  const text = value;
  for (const match of text.matchAll(URL_TOKEN_REGEX)) {
    const url = match[0];
    const start = match.index ?? 0;
    if (start > lastIndex) {
      parts.push(text.slice(lastIndex, start));
    }
    // Strip trailing punctuation that's almost certainly sentence terminators
    // rather than part of the URL.
    let cleaned = url;
    let trailing = '';
    while (
      cleaned.length > 0 &&
      '.,);:!?'.includes(cleaned[cleaned.length - 1])
    ) {
      trailing = cleaned[cleaned.length - 1] + trailing;
      cleaned = cleaned.slice(0, -1);
    }
    const href = cleaned.startsWith('http') ? cleaned : `https://${cleaned}`;
    parts.push(
      <a
        key={`url-${key++}`}
        href={href}
        target='_blank'
        rel='noopener noreferrer'
        className='link'
      >
        {cleaned}
      </a>,
    );
    if (trailing) parts.push(trailing);
    lastIndex = start + url.length;
  }
  if (lastIndex < text.length) {
    parts.push(text.slice(lastIndex));
  }
  return parts.length > 0 ? parts : value;
};

const sortIcon = (direction: SortDirection, active = true) => {
  const isAsc = direction === 'asc';
  return (
    <svg
      xmlns='http://www.w3.org/2000/svg'
      width='16'
      height='16'
      viewBox='0 0 16 16'
      fill='none'
      aria-hidden='true'
      className={active ? 'active' : ''}
    >
      <path
        d={
          isAsc
            ? 'M8 12.6666V3.33331M8 3.33331L3.33333 7.99998M8 3.33331L12.6667 7.99998'
            : 'M8 3.33331V12.6666M8 12.6666L3.33333 7.99998M8 12.6666L12.6667 7.99998'
        }
        stroke='currentColor'
        strokeWidth='1.33'
        strokeLinecap='round'
        strokeLinejoin='round'
      />
    </svg>
  );
};

const ProductScreeningResultsTable: React.FC<
  IProductScreeningResultsTableProps
> = ({ results, page, setPage, handleSubmit }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedHit, setSelectedHit] = useState<any>(null);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [sortConfig, setSortConfig] = useState<{
    key: ProductSortKey | '';
    direction: SortDirection;
  }>({
    key: '',
    direction: 'asc',
  });
  const [tableFilters, setTableFilters] = useState<
    Record<ProductFilterKey, string[]>
  >({
    hsCode: [],
    listName: [],
    agency: [],
    sourceCountry: [],
  });
  const [draftTableFilters, setDraftTableFilters] = useState<
    Record<ProductFilterKey, string[]>
  >({
    hsCode: [],
    listName: [],
    agency: [],
    sourceCountry: [],
  });

  const hits = useMemo(
    () =>
      (results?.data || []).flatMap(
        (row: any) => row?.result?.body?.hits?.hits || [],
      ),
    [results],
  );
  const totalResults = (results?.data || []).reduce(
    (total: number, row: any) =>
      total + (row?.result?.body?.hits?.total?.value || 0),
    0,
  );

  const rows = useMemo(
    () =>
      hits.map((hit: any) => {
        const source = getSource(hit);
        return {
          hit: hit,
          source: source,
          productName: getValue(source, ['Product Name']),
          hsCode: getValue(source, ['HS Code']),
          listName: getValue(source, ['List Name']),
          agency: getValue(source, ['Agency']),
          sourceCountry: getValue(source, [
            'Source Country',
            'BITE-Identified Risk Country',
          ]),
          detectionDate: getValue(source, [
            'Date Updated on BITE Platform',
            'Date Updated',
          ]),
        };
      }),
    [hits],
  );

  const filterOptions = useMemo(() => {
    const buildOptions = (key: ProductFilterKey) =>
      Array.from(
        new Set(
          rows
            .map((row: any) => row[key])
            .filter((value: string) => value && value !== '--'),
        ),
      ).sort((a: any, b: any) => String(a).localeCompare(String(b)));

    return {
      hsCode: buildOptions('hsCode'),
      listName: buildOptions('listName'),
      agency: buildOptions('agency'),
      sourceCountry: buildOptions('sourceCountry'),
    };
  }, [rows]);

  const filteredRows = rows.filter((row: any) => {
    const query = searchQuery.trim().toLowerCase();
    const searchMatch =
      !query ||
      [
        row.productName,
        row.hsCode,
        row.listName,
        row.agency,
        row.sourceCountry,
      ]
        .filter(Boolean)
        .some((field: string) => String(field).toLowerCase().includes(query));

    const hsCodeMatch =
      tableFilters.hsCode.length === 0 ||
      tableFilters.hsCode.includes(row.hsCode);
    const listNameMatch =
      tableFilters.listName.length === 0 ||
      tableFilters.listName.includes(row.listName);
    const agencyMatch =
      tableFilters.agency.length === 0 ||
      tableFilters.agency.includes(row.agency);
    const sourceCountryMatch =
      tableFilters.sourceCountry.length === 0 ||
      tableFilters.sourceCountry.includes(row.sourceCountry);

    return (
      searchMatch &&
      hsCodeMatch &&
      listNameMatch &&
      agencyMatch &&
      sourceCountryMatch
    );
  });

  const sortedRows = useMemo(() => {
    if (!sortConfig.key) {
      return filteredRows;
    }

    return [...filteredRows].sort((a: any, b: any) => {
      const aValue = a[sortConfig.key as ProductSortKey];
      const bValue = b[sortConfig.key as ProductSortKey];
      const directionMultiplier = sortConfig.direction === 'asc' ? 1 : -1;
      if (sortConfig.key === 'detectionDate') {
        const aTime = Date.parse(aValue);
        const bTime = Date.parse(bValue);
        if (!Number.isNaN(aTime) && !Number.isNaN(bTime)) {
          return (aTime - bTime) * directionMultiplier;
        }
      }
      return (
        String(aValue || '').localeCompare(String(bValue || '')) *
        directionMultiplier
      );
    });
  }, [filteredRows, sortConfig]);

  const pageSize = 50;
  const pageCount = Math.min(
    Math.max(1, Math.floor(10000 / pageSize)),
    Math.max(1, Math.ceil(totalResults / pageSize)),
  );
  const currentPage = Math.min(Math.max(page, 0), pageCount - 1);
  const activeFilterCount = Object.values(tableFilters).reduce(
    (total, values) => total + values.length,
    0,
  );

  const selectedSource = selectedHit ? getSource(selectedHit) : {};
  const { signals: selectedRiskSignals, rest: selectedDetails } =
    splitRiskSignals(selectedSource);
  const selectedRowIndex = selectedHit
    ? sortedRows.findIndex((row: any) => row.hit === selectedHit)
    : -1;
  const canNavigatePrev = selectedRowIndex > 0;
  const canNavigateNext =
    selectedRowIndex >= 0 && selectedRowIndex < sortedRows.length - 1;

  const columnHeaders: Array<{ key: ProductSortKey; label: string }> = [
    { key: 'productName', label: 'Product Name' },
    { key: 'hsCode', label: 'HS Code' },
    { key: 'listName', label: 'List Name' },
    { key: 'agency', label: 'Agency' },
    { key: 'sourceCountry', label: 'Risk Country' },
    { key: 'detectionDate', label: 'Detection Date' },
  ];

  const handleSort = (key: ProductSortKey) => {
    setSortConfig((prev) => ({
      key: key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc',
    }));
  };

  const getSortIcon = (key: ProductSortKey) => {
    if (sortConfig.key !== key) {
      return sortIcon('asc', false);
    }
    return sortIcon(sortConfig.direction);
  };

  const handlePaginationChange = (
    _event: React.ChangeEvent<unknown>,
    value: number,
  ) => {
    const nextPage = Math.max(0, Math.min(pageCount - 1, value - 1));
    if (nextPage === currentPage) {
      return;
    }
    setPage(nextPage);
    handleSubmit(nextPage, false);
  };

  const handleOpenFilters = () => {
    setDraftTableFilters(tableFilters);
    setFiltersOpen(true);
  };

  const toggleDraftFilterValue = (key: ProductFilterKey, value: string) => {
    setDraftTableFilters((prev) => {
      const selected = prev[key];
      const nextSelected = selected.includes(value)
        ? selected.filter((item) => item !== value)
        : [...selected, value];
      return { ...prev, [key]: nextSelected };
    });
  };

  const clearTableFilters = () => {
    const cleared = {
      hsCode: [],
      listName: [],
      agency: [],
      sourceCountry: [],
    };
    setTableFilters(cleared);
    setDraftTableFilters(cleared);
  };

  const handleApplyFilters = () => {
    setTableFilters(draftTableFilters);
    setFiltersOpen(false);
  };

  const handleResetResults = () => {
    setSearchQuery('');
    setSortConfig({ key: '', direction: 'asc' });
    clearTableFilters();
    setSelectedHit(null);
    setIsDrawerOpen(false);
    setFiltersOpen(false);
    setPage(0);
    handleSubmit(0, false);
  };

  const handleDrawerPrev = () => {
    if (canNavigatePrev) {
      setSelectedHit(sortedRows[selectedRowIndex - 1].hit);
    }
  };

  const handleDrawerNext = () => {
    if (canNavigateNext) {
      setSelectedHit(sortedRows[selectedRowIndex + 1].hit);
    }
  };

  const renderCell = (row: any, key: ProductSortKey) => {
    switch (key) {
      case 'productName':
        return <td className='entity-results-name'>{row.productName}</td>;
      case 'hsCode':
        return <td>{row.hsCode}</td>;
      case 'listName':
        return <td>{row.listName}</td>;
      case 'agency':
        return <td>{row.agency}</td>;
      case 'sourceCountry':
        return <td>{row.sourceCountry}</td>;
      case 'detectionDate':
        return <td>{row.detectionDate}</td>;
      default:
        return <td>--</td>;
    }
  };

  return (
    <>
      <div className='entity-results-toolbar'>
        <div className='entity-results-search'>
          <SearchIcon sx={{ fontSize: 19 }} />
          <input
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder='Search products, HTS, list...'
          />
          {searchQuery && (
            <button
              type='button'
              className='entity-results-search-clear'
              aria-label='Clear product results search'
              onClick={() => setSearchQuery('')}
            >
              <CloseIcon sx={{ fontSize: 18 }} />
            </button>
          )}
        </div>
        <div className='entity-results-tabs'>
          <button type='button' className='active'>
            All Matches
            <span>{Number(totalResults || 0).toLocaleString()}</span>
          </button>
        </div>
        <div className='entity-results-actions'>
          <button
            type='button'
            className={activeFilterCount > 0 ? 'active' : ''}
            onClick={handleOpenFilters}
          >
            <FilterListIcon sx={{ fontSize: 20 }} />
            Filters
            {activeFilterCount > 0 && <span>{activeFilterCount}</span>}
          </button>
          <button
            type='button'
            aria-label='Reset product results table'
            onClick={handleResetResults}
          >
            <RefreshIcon sx={{ fontSize: 20 }} />
          </button>
        </div>
      </div>

      <Modal open={filtersOpen} onClose={() => setFiltersOpen(false)}>
        <div className='entity-results-filter-modal'>
          <div className='entity-results-filter-modal-header'>
            <div className='entity-results-filter-modal-header-content'>
              <div className='entity-results-filter-modal-icon'>
                <FilterListIcon sx={{ fontSize: 24 }} />
              </div>
              <div className='entity-results-filter-modal-copy'>
                <h2>Product Table Filters</h2>
                <p>
                  Choose HTS codes, lists, agencies, and countries to narrow
                  the current table.
                </p>
              </div>
            </div>
            <button
              type='button'
              className='entity-results-filter-modal-close'
              onClick={() => setFiltersOpen(false)}
            >
              <CloseIcon sx={{ fontSize: 28 }} />
            </button>
          </div>

          <div className='entity-results-filter-modal-body'>
            {[
              {
                key: 'hsCode' as ProductFilterKey,
                label: 'HS Code',
                options: filterOptions.hsCode,
              },
              {
                key: 'listName' as ProductFilterKey,
                label: 'List Name',
                options: filterOptions.listName,
              },
              {
                key: 'agency' as ProductFilterKey,
                label: 'Agency',
                options: filterOptions.agency,
              },
              {
                key: 'sourceCountry' as ProductFilterKey,
                label: 'Risk Country',
                options: filterOptions.sourceCountry,
              },
            ].map((section) => (
              <div
                className='entity-results-filter-modal-column'
                key={section.key}
              >
                <div className='entity-results-filter-modal-column-header'>
                  <FilterListIcon sx={{ fontSize: 18 }} />
                  <h3>{section.label}</h3>
                </div>
                <div className='entity-results-filter-modal-options'>
                  {section.options.length > 0 ? (
                    section.options.map((option: any) => (
                      <button
                        type='button'
                        key={option}
                        className='entity-results-filter-modal-option'
                        onClick={() =>
                          toggleDraftFilterValue(section.key, option)
                        }
                      >
                        <span className='bill-container-fit-screen-content-row-input'>
                          <span
                            className={`bill-container-fit-screen-content-row-input-icon ${
                              draftTableFilters[section.key].includes(option)
                                ? 'selected'
                                : ''
                            }`}
                          >
                            <span
                              className={`bill-container-fit-screen-content-row-input-icon-rnd ${
                                draftTableFilters[section.key].includes(option)
                                  ? 'selected'
                                  : ''
                              }`}
                            />
                          </span>
                        </span>
                        <span className='entity-results-filter-modal-option-text'>
                          {option}
                        </span>
                      </button>
                    ))
                  ) : (
                    <p>No options on this page</p>
                  )}
                </div>
              </div>
            ))}
          </div>

          <div className='entity-results-filter-modal-footer'>
            <button type='button' onClick={clearTableFilters}>
              Clear
            </button>
            <button type='button' onClick={handleApplyFilters}>
              Apply
            </button>
          </div>
        </div>
      </Modal>

      <div className='entity-results-table-card'>
        {results?.isLoading && (
          <div className='entity-results-table-loading'>
            <CircularProgress size={34} style={{ color: '#0E8BFF' }} />
          </div>
        )}
        <div className='entity-results-table-scroll'>
          <table className='entity-results-table'>
            <thead>
              <tr>
                {columnHeaders.map((column) => (
                  <th key={column.key}>
                    <button
                      type='button'
                      className='entity-results-sort-header'
                      onClick={() => handleSort(column.key)}
                    >
                      {column.label}
                      {getSortIcon(column.key)}
                    </button>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {sortedRows.map((row: any, index: number) => {
                const isSelected = selectedHit === row.hit;
                return (
                  <tr
                    key={`${row.productName}-${row.hsCode}-${index}`}
                    className={isSelected ? 'selected-row' : ''}
                    onClick={() => {
                      setSelectedHit(row.hit);
                      setIsDrawerOpen(true);
                    }}
                    style={{
                      ...(isSelected
                        ? {
                            backgroundColor: 'rgba(72, 149, 239, 0.12)',
                            borderLeft: '3px solid var(--action-color)',
                          }
                        : {
                            borderLeft: '3px solid transparent',
                          }),
                    }}
                  >
                    {columnHeaders.map((column) => (
                      <React.Fragment key={column.key}>
                        {renderCell(row, column.key)}
                      </React.Fragment>
                    ))}
                  </tr>
                );
              })}
              {sortedRows.length === 0 && (
                <tr>
                  <td
                    colSpan={columnHeaders.length}
                    className='entity-results-empty'
                  >
                    No product matches found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <div className='entity-results-footer'>
          <span>
            Showing {sortedRows.length ? currentPage * pageSize + 1 : 0} to{' '}
            {Math.min(currentPage * pageSize + sortedRows.length, totalResults)}{' '}
            of {Number(totalResults || 0).toLocaleString()} results
          </span>
          <div className='entity-results-pagination hb-pagination'>
            <Pagination
              count={pageCount}
              page={currentPage + 1}
              onChange={handlePaginationChange}
              color='primary'
            />
          </div>
        </div>
      </div>

      {selectedHit && isDrawerOpen && (
        <div className='drawer-backdrop' onClick={() => setIsDrawerOpen(false)}>
          <aside
            className='drawer-panel entity-record-drawer product-record-drawer'
            onClick={(e) => e.stopPropagation()}
          >
            <div className='drawer-header entity-record-drawer-header'>
              <div className='entity-record-drawer-nav'>
                <button
                  type='button'
                  onClick={handleDrawerPrev}
                  disabled={!canNavigatePrev}
                  title='Open previous product'
                >
                  <ChevronLeftIcon sx={{ fontSize: 22 }} />
                </button>
                <button
                  type='button'
                  onClick={handleDrawerNext}
                  disabled={!canNavigateNext}
                  title='Open next product'
                >
                  <ChevronRightIcon sx={{ fontSize: 22 }} />
                </button>
              </div>
              <h2 className='drawer-title entity-record-drawer-title'>
                <span className='entity-record-drawer-title-main'>
                  {selectedSource['Product Name'] || 'Product Details'}
                </span>
              </h2>
              <button
                type='button'
                className='drawer-close-btn entity-record-drawer-close'
                onClick={() => setIsDrawerOpen(false)}
              >
                <CloseIcon sx={{ fontSize: 28 }} />
              </button>
            </div>
            <div className='drawer-body entity-record-drawer-body'>
              {selectedRiskSignals.length > 0 && (
                <div>
                  <div className='drawer-section-title'>Risk Signals</div>
                  <div className='drawer-kv-box entity-record-drawer-kv-box'>
                    {selectedRiskSignals.map(({ label, value }) => (
                      <div
                        className='drawer-kv-row entity-record-drawer-row'
                        key={label}
                      >
                        <span className='drawer-kv-label'>{label}</span>
                        <span className='drawer-kv-value'>
                          {renderFieldWithLinks(formatFieldValue(value))}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              <div>
                <div className='drawer-section-title'>Product Details</div>
                <div className='drawer-kv-box entity-record-drawer-kv-box'>
                  {Object.entries(selectedDetails)
                    .filter(([, value]) => hasDisplayValue(value))
                    .map(([label, value]) => (
                      <div
                        className='drawer-kv-row entity-record-drawer-row'
                        key={label}
                      >
                        <span className='drawer-kv-label'>{label}</span>
                        <span className='drawer-kv-value'>
                          {renderFieldWithLinks(formatFieldValue(value))}
                        </span>
                      </div>
                    ))}
                </div>
              </div>
            </div>
          </aside>
        </div>
      )}
    </>
  );
};

export default ProductScreeningResultsTable;
